import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, Plus, Trash2, Camera, Check, X, GripVertical, ChevronUp, ChevronDown, Lock, Puzzle, Hash, Calculator, Pencil } from 'lucide-react';
import { MapContainer, GpsAccuracyBadge } from '@/components/map';
import { Button, HuntDeletedModal } from '@/components/common';
import { ParticipantDrawer } from '@/components/hider/ParticipantDrawer';
import { useGeolocation } from '@/hooks/useGeolocation';
import { useSignalR, PinCreatedMessage, PinDeletedMessage, HuntDeletedMessage, ParticipantJoinedMessage, ParticipantLeftMessage, ParticipantRemovedMessage } from '@/hooks/useSignalR';
import { useHuntStore } from '@/stores/huntStore';
import { useAuthStore } from '@/stores/authStore';
import { huntService } from '@/services/api/huntService';
import { hunterService } from '@/services/api/hunterService';
import { pinService } from '@/services/api/pinService';
import { Pin, PinValidation, isValidGpsAccuracy, HuntStatus } from '@gps-hunt/shared';
import { HiderActiveView } from '@/components/hider/HiderActiveView';

// Local type definitions (mirrors @gps-hunt/shared)
type ChallengeType = 'riddle' | 'hex_code' | 'numerical';
interface PinChallenge {
  type: ChallengeType;
  question: string;
  answer: string;
}

// Extended Pin type with challenge info for hider view
interface PinWithChallenge extends Pin {
  challenge?: PinChallenge;
}

// Azure Maps subscription key from environment
const AZURE_MAPS_KEY = import.meta.env.VITE_AZURE_MAPS_KEY || '';

interface PinConfigModalProps {
  position: { latitude: number; longitude: number; accuracy: number } | null;
  editingPin?: PinWithChallenge | null;
  onSave: (itemCount: number, photo?: File, challenge?: PinChallenge) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}

/**
 * Pin configuration modal for setting item count, photo, and optional challenge
 */
const PinConfigModal: React.FC<PinConfigModalProps> = ({
  position,
  editingPin,
  onSave,
  onCancel,
  isSaving,
}) => {
  const [itemCount, setItemCount] = useState(1);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  
  // Challenge state
  const [challengeEnabled, setChallengeEnabled] = useState(false);
  const [challengeType, setChallengeType] = useState<ChallengeType>('riddle');
  const [challengeQuestion, setChallengeQuestion] = useState('');
  const [challengeAnswer, setChallengeAnswer] = useState('');

  // Reset form when modal opens with a new position or editing pin
  useEffect(() => {
    if (editingPin) {
      // Edit mode - populate with existing values
      setItemCount(editingPin.itemCount);
      setPhoto(null);
      setPhotoPreview(editingPin.hintPhotoUrl || null);
      if (editingPin.challenge) {
        setChallengeEnabled(true);
        setChallengeType(editingPin.challenge.type as ChallengeType);
        setChallengeQuestion(editingPin.challenge.question);
        setChallengeAnswer(editingPin.challenge.answer || ''); // Show existing answer for hider
      } else {
        setChallengeEnabled(false);
        setChallengeType('riddle');
        setChallengeQuestion('');
        setChallengeAnswer('');
      }
    } else if (position) {
      // Create mode - reset to defaults
      setItemCount(1);
      setPhoto(null);
      setPhotoPreview(null);
      setChallengeEnabled(false);
      setChallengeType('riddle');
      setChallengeQuestion('');
      setChallengeAnswer('');
    }
  }, [position, editingPin]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    const challenge = challengeEnabled && challengeQuestion && challengeAnswer 
      ? { type: challengeType, question: challengeQuestion, answer: challengeAnswer }
      : undefined;
    await onSave(itemCount, photo || undefined, challenge);
  };

  // Validate challenge answer format
  const isChallengeValid = !challengeEnabled || (
    challengeQuestion.trim() !== '' &&
    challengeAnswer.trim() !== '' &&
    (challengeType !== 'hex_code' || /^[0-9A-Fa-f]{4}$/.test(challengeAnswer)) &&
    (challengeType !== 'numerical' || !isNaN(parseFloat(challengeAnswer)))
  );

  if (!position && !editingPin) return null;

  const isEditMode = !!editingPin;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-end justify-center z-50">
      <div className="bg-secondary rounded-t-2xl w-full max-w-lg p-6 pb-safe-bottom animate-slide-up max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold text-center mb-4">{isEditMode ? 'EDIT PIN' : 'CONFIGURE PIN'}</h2>

        {!isEditMode && position && (
          <div className="text-sm text-text-secondary text-center mb-6">
            Location locked at:
            <br />
            {position.latitude.toFixed(4)}°N, {position.longitude.toFixed(4)}°W (±
            {position.accuracy.toFixed(1)}m)
          </div>
        )}
        {isEditMode && (
          <div className="text-sm text-text-secondary text-center mb-6">
            Editing pin location cannot be changed.
            <br />
            Delete and create a new pin to change location.
          </div>
        )}

        <div className="border-t border-white/10 pt-4 mb-4">
          <label className="block text-sm text-text-secondary mb-2">
            How many items are hidden here?
          </label>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setItemCount(Math.max(1, itemCount - 1))}
              className="w-12 h-12 rounded-full bg-background flex items-center justify-center
                         text-2xl hover:bg-primary/20 transition-colors"
              disabled={itemCount <= 1}
            >
              -
            </button>
            <div className="flex-1 flex justify-center gap-1">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className={`w-3 h-3 rounded-full ${
                    i < itemCount ? 'bg-primary' : 'bg-white/20'
                  }`}
                />
              ))}
            </div>
            <span className="text-2xl font-mono w-8 text-center">{itemCount}</span>
            <button
              onClick={() => setItemCount(Math.min(10, itemCount + 1))}
              className="w-12 h-12 rounded-full bg-background flex items-center justify-center
                         text-2xl hover:bg-primary/20 transition-colors"
              disabled={itemCount >= 10}
            >
              +
            </button>
          </div>
        </div>

        <div className="border-t border-white/10 pt-4 mb-6">
          <label className="block text-sm text-text-secondary mb-2">
            Hint Photo (Optional)
          </label>
          {photoPreview ? (
            <div className="relative">
              <img
                src={photoPreview}
                alt="Hint preview"
                className="w-full h-40 object-cover rounded-lg"
              />
              <button
                onClick={() => {
                  setPhoto(null);
                  setPhotoPreview(null);
                }}
                className="absolute top-2 right-2 w-8 h-8 bg-black/50 rounded-full
                           flex items-center justify-center"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <label className="block">
              <div
                className="w-full h-20 border-2 border-dashed border-white/30 rounded-lg
                           flex items-center justify-center gap-2 text-text-secondary
                           hover:border-primary/50 hover:text-primary transition-colors cursor-pointer"
              >
                <Camera className="w-6 h-6" />
                <span>ADD PHOTO</span>
              </div>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                className="hidden"
              />
            </label>
          )}
        </div>

        {/* Challenge Configuration */}
        <div className="border-t border-white/10 pt-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <Lock className="w-4 h-4" />
              Add Pin Challenge (Optional)
            </label>
            <button
              onClick={() => setChallengeEnabled(!challengeEnabled)}
              className={`w-12 h-6 rounded-full transition-colors ${
                challengeEnabled ? 'bg-primary' : 'bg-white/20'
              }`}
            >
              <div className={`w-5 h-5 rounded-full bg-white transform transition-transform ${
                challengeEnabled ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
          
          {challengeEnabled && (
            <div className="space-y-3 animate-slide-up">
              {/* Challenge Type Selector */}
              <div className="grid grid-cols-3 gap-2">
                {(['riddle', 'hex_code', 'numerical'] as ChallengeType[]).map((type) => (
                  <button
                    key={type}
                    onClick={() => {
                      setChallengeType(type);
                      setChallengeAnswer('');
                    }}
                    className={`py-2 px-3 rounded-lg text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
                      challengeType === type 
                        ? 'bg-primary text-white' 
                        : 'bg-background text-text-secondary hover:bg-background/80'
                    }`}
                  >
                    {type === 'riddle' && <Puzzle className="w-3.5 h-3.5" />}
                    {type === 'hex_code' && <Hash className="w-3.5 h-3.5" />}
                    {type === 'numerical' && <Calculator className="w-3.5 h-3.5" />}
                    {type === 'hex_code' ? 'HEX CODE' : type.toUpperCase()}
                  </button>
                ))}
              </div>
              
              {/* Question Input */}
              <div>
                <label className="block text-xs text-text-muted mb-1">
                  {challengeType === 'riddle' && 'Riddle (hunters must answer with one word)'}
                  {challengeType === 'hex_code' && 'Hint for finding the 4-char hex code'}
                  {challengeType === 'numerical' && 'Question with a numerical answer'}
                </label>
                <input
                  type="text"
                  value={challengeQuestion}
                  onChange={(e) => setChallengeQuestion(e.target.value)}
                  placeholder={
                    challengeType === 'riddle' ? 'e.g., "I have keys but no locks..."' :
                    challengeType === 'hex_code' ? 'e.g., "Look under the bench"' :
                    'e.g., "How many steps to the fountain?"'
                  }
                  className="w-full px-3 py-2 rounded-lg bg-background text-text-primary 
                             placeholder:text-text-muted text-sm"
                />
              </div>
              
              {/* Answer Input */}
              <div>
                <label className="block text-xs text-text-muted mb-1">
                  {challengeType === 'riddle' && 'Answer (single word)'}
                  {challengeType === 'hex_code' && 'Hex Code (4 characters: 0-9, A-F)'}
                  {challengeType === 'numerical' && 'Correct Number'}
                </label>
                <input
                  type={challengeType === 'numerical' ? 'number' : 'text'}
                  value={challengeAnswer}
                  onChange={(e) => setChallengeAnswer(e.target.value.toUpperCase())}
                  placeholder={
                    challengeType === 'riddle' ? 'e.g., "keyboard"' :
                    challengeType === 'hex_code' ? 'e.g., "A7F2"' :
                    'e.g., "42"'
                  }
                  maxLength={challengeType === 'hex_code' ? 4 : undefined}
                  className={`w-full px-3 py-2 rounded-lg bg-background text-text-primary 
                             placeholder:text-text-muted text-sm ${
                    challengeType === 'hex_code' ? 'uppercase font-mono tracking-wider' : ''
                  }`}
                />
                {isEditMode && editingPin?.challenge && !challengeAnswer && (
                  <p className="text-xs text-amber-400 mt-1">Re-enter answer to update challenge</p>
                )}
                {challengeType === 'hex_code' && challengeAnswer && !/^[0-9A-Fa-f]{4}$/.test(challengeAnswer) && (
                  <p className="text-xs text-red-400 mt-1">Must be exactly 4 hex characters (0-9, A-F)</p>
                )}
                {challengeType === 'numerical' && challengeAnswer && isNaN(parseFloat(challengeAnswer)) && (
                  <p className="text-xs text-red-400 mt-1">Must be a valid number</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <Button variant="secondary" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !isChallengeValid} className="flex-1">
            {isSaving ? 'Saving...' : (isEditMode ? 'UPDATE PIN' : 'SAVE PIN')}
          </Button>
        </div>
      </div>
    </div>
  );
};

interface PinCardProps {
  pin: PinWithChallenge & { hasPhoto?: boolean };
  index: number;
  onEdit: () => void;
  onDelete: () => void;
  onDragStart?: (index: number) => void;
  onDragOver?: (index: number) => void;
  onDragEnd?: () => void;
  isDragging?: boolean;
  isDragOver?: boolean;
}

/**
 * Compact pin list item for footer with drag handle
 */
const PinCard: React.FC<PinCardProps> = ({ 
  pin, 
  index, 
  onEdit,
  onDelete,
  onDragStart,
  onDragOver,
  onDragEnd,
  isDragging,
  isDragOver,
}) => {
  return (
    <div 
      className={`flex items-center gap-2 p-2 bg-background/50 rounded-lg transition-all ${
        isDragging ? 'opacity-50 scale-95' : ''
      } ${isDragOver ? 'ring-2 ring-primary' : ''}`}
      draggable
      onDragStart={() => onDragStart?.(index)}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver?.(index);
      }}
      onDragEnd={() => onDragEnd?.()}
    >
      {/* Drag handle */}
      <div 
        className="cursor-grab active:cursor-grabbing text-text-muted hover:text-text-secondary touch-none"
        onTouchStart={(e) => {
          // For touch devices, we'll use a different approach
          e.stopPropagation();
        }}
      >
        <GripVertical className="w-4 h-4" />
      </div>
      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-xs font-semibold flex-shrink-0">
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-xs flex items-center gap-1">
          🎯 {pin.itemCount} {pin.hintPhotoUrl ? '📸' : ''}
          {pin.challenge && (
            <span title={`Challenge: ${pin.challenge.type}`}>
              {pin.challenge.type === 'riddle' && <Puzzle className="w-3.5 h-3.5 text-purple-400 inline" />}
              {pin.challenge.type === 'hex_code' && <Hash className="w-3.5 h-3.5 text-cyan-400 inline" />}
              {pin.challenge.type === 'numerical' && <Calculator className="w-3.5 h-3.5 text-amber-400 inline" />}
            </span>
          )}
        </span>
      </div>
      <button
        onClick={onEdit}
        className="w-7 h-7 flex items-center justify-center text-text-secondary
                   hover:text-blue-400 transition-colors flex-shrink-0"
        aria-label="Edit pin"
      >
        <Pencil className="w-4 h-4" />
      </button>
      <button
        onClick={onDelete}
        className="w-7 h-7 flex items-center justify-center text-text-secondary
                   hover:text-primary transition-colors flex-shrink-0"
        aria-label="Delete pin"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
};

/**
 * Hider page - Map view for placing pins
 */
export const HiderPage: React.FC = () => {
  const { huntId } = useParams<{ huntId: string }>();
  const navigate = useNavigate();
  const { position, isLoading: gpsLoading, startWatching, stopWatching } = useGeolocation();
  const { currentHunt, setCurrentHunt, setLoading, setError, reset } = useHuntStore();
  const token = useAuthStore((state) => state.token);
  const authLoading = useAuthStore((state) => state.isLoading);
  const currentUser = useAuthStore((state) => state.user);

  const [pins, setPins] = useState<PinWithChallenge[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pendingPin, setPendingPin] = useState<{
    latitude: number;
    longitude: number;
    accuracy: number;
  } | null>(null);
  const [editingPin, setEditingPin] = useState<PinWithChallenge | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isMarkingReady, setIsMarkingReady] = useState(false);
  const [huntDeletedInfo, setHuntDeletedInfo] = useState<{ huntName: string; message: string } | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [participants, setParticipants] = useState<Array<{
    odifiedUserId: string;
    displayName: string;
    role: 'hunter' | 'hider' | 'spectator';
    isGuest?: boolean;
  }>>([]);
  const [isPinListExpanded, setIsPinListExpanded] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // SignalR connection for real-time pin updates
  const handlePinCreated = useCallback((msg: PinCreatedMessage) => {
    // Add new pin to the list if it's not already there
    setPins(currentPins => {
      const exists = currentPins.some(p => p.id === msg.pinId);
      if (exists) return currentPins;
      
      const newPin: Pin = {
        id: msg.pinId,
        huntId: huntId || '',
        latitude: msg.latitude,
        longitude: msg.longitude,
        accuracy: 10, // Default, not provided in message
        itemCount: msg.itemCount,
        sequenceOrder: msg.sequenceOrder,
        createdBy: msg.createdBy,
        createdAt: new Date().toISOString(),
      };
      return [...currentPins, newPin];
    });
  }, [huntId]);

  const handlePinDeleted = useCallback((msg: PinDeletedMessage) => {
    setPins(currentPins => currentPins.filter(p => p.id !== msg.pinId));
  }, []);

  const handleHuntDeleted = useCallback((msg: HuntDeletedMessage) => {
    reset(); // Clear hunt state immediately to remove join code from header
    setHuntDeletedInfo({ huntName: msg.huntName, message: msg.message });
  }, [reset]);

  const handleParticipantJoined = useCallback((msg: ParticipantJoinedMessage) => {
    setParticipants(prev => {
      if (prev.some(p => p.odifiedUserId === msg.userId)) return prev;
      return [...prev, {
        odifiedUserId: msg.userId,
        displayName: msg.displayName,
        role: msg.role,
        isGuest: msg.isGuest,
      }];
    });
  }, []);

  const handleParticipantLeft = useCallback((msg: ParticipantLeftMessage) => {
    setParticipants(prev => prev.filter(p => p.odifiedUserId !== msg.userId));
  }, []);

  const handleParticipantRemoved = useCallback((msg: ParticipantRemovedMessage) => {
    setParticipants(prev => prev.filter(p => p.odifiedUserId !== msg.userId));
  }, []);

  const { connect, disconnect } = useSignalR({
    onPinCreated: handlePinCreated,
    onPinDeleted: handlePinDeleted,
    onHuntDeleted: handleHuntDeleted,
    onParticipantJoined: handleParticipantJoined,
    onParticipantLeft: handleParticipantLeft,
    onParticipantRemoved: handleParticipantRemoved,
  });

  // Connect to SignalR when hunt is loaded
  useEffect(() => {
    if (huntId) {
      connect(huntId);
    }
    return () => {
      disconnect();
    };
  }, [huntId, connect, disconnect]);

  // Start GPS tracking on mount
  useEffect(() => {
    startWatching();
    return () => stopWatching();
  }, [startWatching, stopWatching]);

  // Track hider setup walk distance by sending location to server during SETUP
  useEffect(() => {
    if (!huntId || !position) return;
    // Only track during setup phase
    if (!currentHunt || (currentHunt.status !== HuntStatus.SETUP && currentHunt.status !== HuntStatus.STARTING)) return;

    const interval = setInterval(() => {
      hunterService.updateLocation(huntId, {
        latitude: position.latitude,
        longitude: position.longitude,
        accuracy: position.accuracy,
        heading: position.heading,
        speed: position.speed,
      }).catch(() => {
        // Silently ignore — walk tracking is best-effort during setup
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [huntId, position, currentHunt?.status]);

  // Fetch hunt and pins
  useEffect(() => {
    const fetchData = async () => {
      // Wait for auth to initialize and token to be available
      if (!huntId || authLoading || !token) {
        return;
      }

      try {
        setLoading(true);
        const huntData = await huntService.getHunt(huntId);
        setCurrentHunt(huntData.hunt);

        // Load participants
        setParticipants(huntData.participants.map(p => ({
          odifiedUserId: p.userId,
          displayName: p.displayName,
          role: p.role,
          isGuest: p.isGuest,
        })));

        const pinsData = await pinService.getPins(huntId);
        // Convert to PinWithChallenge array
        const pinArray: PinWithChallenge[] = pinsData.pins.map((p) => ({
          id: p.id,
          huntId: huntId,
          latitude: p.latitude,
          longitude: p.longitude,
          accuracy: p.accuracy,
          itemCount: p.itemCount,
          hintPhotoUrl: p.hintPhotoUrl,
          sequenceOrder: p.sequenceOrder,
          createdBy: '',
          createdAt: '',
          challenge: p.challenge ? {
            type: p.challenge.type as ChallengeType,
            question: p.challenge.question,
            answer: p.challenge.answer,
          } : undefined,
        }));
        setPins(pinArray);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load hunt');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [huntId, token, authLoading, setCurrentHunt, setLoading, setError]);

  // Handle drop pin button click
  const handleDropPin = useCallback(() => {
    if (!position || !isValidGpsAccuracy(position.accuracy)) {
      return;
    }

    setPendingPin({
      latitude: position.latitude,
      longitude: position.longitude,
      accuracy: position.accuracy,
    });
  }, [position]);

  // Handle pin save (create or update)
  const handleSavePin = async (itemCount: number, photo?: File, challenge?: PinChallenge) => {
    if (!huntId) return;

    setIsSaving(true);
    try {
      setLocalError(null);
      
      if (editingPin) {
        // Update existing pin
        await pinService.updatePin(huntId, editingPin.id, {
          itemCount,
          challenge: challenge || null,
        });

        // Upload new photo if provided
        if (photo) {
          const uploadInfo = await pinService.getPhotoUploadUrl(huntId, editingPin.id);
          await pinService.uploadPhoto(uploadInfo.uploadUrl, photo);
          await pinService.confirmPhotoUpload(huntId, editingPin.id, uploadInfo.blobName);
        }

        // Refresh pins
        const pinsData = await pinService.getPins(huntId);
        const pinArray: PinWithChallenge[] = pinsData.pins.map((p) => ({
          id: p.id,
          huntId: huntId,
          latitude: p.latitude,
          longitude: p.longitude,
          accuracy: p.accuracy,
          itemCount: p.itemCount,
          hintPhotoUrl: p.hintPhotoUrl,
          sequenceOrder: p.sequenceOrder,
          createdBy: '',
          createdAt: '',
          challenge: p.challenge ? {
            type: p.challenge.type as ChallengeType,
            question: p.challenge.question,
            answer: p.challenge.answer,
          } : undefined,
        }));
        setPins(pinArray);

        // Close modal and show success message
        setEditingPin(null);
        setSuccessMessage(`Pin updated! ${itemCount} item${itemCount > 1 ? 's' : ''} hidden.`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else if (pendingPin) {
        // Create new pin
        const result = await pinService.createPin(huntId, {
          latitude: pendingPin.latitude,
          longitude: pendingPin.longitude,
          accuracy: pendingPin.accuracy,
          itemCount,
          challenge,
        });

        // Upload photo if provided
        if (photo) {
          const uploadInfo = await pinService.getPhotoUploadUrl(huntId, result.pinId);
          await pinService.uploadPhoto(uploadInfo.uploadUrl, photo);
          await pinService.confirmPhotoUpload(huntId, result.pinId, uploadInfo.blobName);
        }

        // Refresh pins
        const pinsData = await pinService.getPins(huntId);
        const pinArray: PinWithChallenge[] = pinsData.pins.map((p) => ({
          id: p.id,
          huntId: huntId,
          latitude: p.latitude,
          longitude: p.longitude,
          accuracy: p.accuracy,
          itemCount: p.itemCount,
          hintPhotoUrl: p.hintPhotoUrl,
          sequenceOrder: p.sequenceOrder,
          createdBy: '',
          createdAt: '',
          challenge: p.challenge ? {
            type: p.challenge.type as ChallengeType,
            question: p.challenge.question,
            answer: p.challenge.answer,
          } : undefined,
        }));
        setPins(pinArray);

        // Close modal and show success message
        setPendingPin(null);
        setSuccessMessage(`Pin #${pinArray.length} created! ${itemCount} item${itemCount > 1 ? 's' : ''} hidden.`);
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to save pin';
      console.error('Failed to save pin:', err);
      setLocalError(errorMsg);
      setError(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle pin edit
  const handleEditPin = (pin: PinWithChallenge) => {
    setEditingPin(pin);
  };

  // Handle pin delete
  const handleDeletePin = async (pinId: string) => {
    if (!huntId) return;

    try {
      await pinService.deletePin(huntId, pinId);
      setPins((prev) => prev.filter((p) => p.id !== pinId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete pin');
    }
  };

  // Handle drag reorder
  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (index: number) => {
    if (dragIndex !== null && dragIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragEnd = async () => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      // Reorder pins locally
      const newPins = [...pins];
      const [draggedPin] = newPins.splice(dragIndex, 1);
      newPins.splice(dragOverIndex, 0, draggedPin);
      setPins(newPins);

      // Update server
      if (huntId) {
        try {
          const newOrder = newPins.map(p => p.id);
          await pinService.reorderPins(huntId, newOrder);
        } catch (err) {
          // Revert on error
          setPins(pins);
          setLocalError('Failed to reorder pins');
        }
      }
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // Handle mark ready
  const handleMarkReady = async () => {
    if (!huntId) return;

    setIsMarkingReady(true);
    try {
      await pinService.markReady(huntId);
      // Always go to lobby to wait for others
      navigate(`/lobby/${huntId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark ready');
      setIsMarkingReady(false);
    }
  };

  const canDropPin =
    position &&
    isValidGpsAccuracy(position.accuracy) &&
    pins.length < 10;

  const canMarkReady = pins.length > 0;

  // Show spectator view when hunt is ACTIVE
  if (currentHunt?.status === HuntStatus.ACTIVE) {
    return <HiderActiveView />;
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Full-page Map */}
      <div className="flex-1 relative">
        <MapContainer
          subscriptionKey={AZURE_MAPS_KEY}
          pins={pins}
          userLocation={
            position
              ? {
                  latitude: position.latitude,
                  longitude: position.longitude,
                  accuracy: position.accuracy,
                }
              : undefined
          }
          center={
            position
              ? [position.longitude, position.latitude]
              : undefined
          }
          className="absolute inset-0"
        />

        {/* Top overlay - GPS accuracy and participants */}
        <div className="absolute top-4 left-4 right-4 flex items-start justify-between pointer-events-none z-10">
          <div className="bg-secondary/90 backdrop-blur-sm px-3 py-2 rounded-xl flex items-center gap-3 pointer-events-auto">
            <GpsAccuracyBadge
              accuracy={position?.accuracy ?? null}
              isLoading={gpsLoading}
            />
            <div className="w-px h-4 bg-white/20" />
            <span className="text-sm">📍 {pins.length}/10</span>
          </div>
          
          <button
            onClick={() => setIsDrawerOpen(true)}
            className="bg-secondary/90 backdrop-blur-sm px-3 py-2 rounded-xl flex items-center gap-2 pointer-events-auto hover:bg-secondary transition-colors"
          >
            <Users className="w-4 h-4" />
            <span className="text-sm">{participants.length}</span>
          </button>
        </div>

        {/* Error/Success banners */}
        {(localError || successMessage) && (
          <div className="absolute top-16 left-4 right-4 z-10 space-y-2">
            {localError && (
              <div className="bg-red-500/90 backdrop-blur-sm p-3 rounded-xl flex items-center justify-between">
                <span className="text-white text-sm">{localError}</span>
                <button onClick={() => setLocalError(null)} className="text-white/80 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            {successMessage && (
              <div className="bg-green-500/90 backdrop-blur-sm p-3 rounded-xl flex items-center justify-between animate-pulse">
                <span className="text-white text-sm flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  {successMessage}
                </span>
                <button onClick={() => setSuccessMessage(null)} className="text-white/80 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Drop pin button - floating above footer */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
          <Button
            onClick={handleDropPin}
            disabled={!canDropPin}
            className="flex items-center gap-2 px-6 shadow-lg"
          >
            <Plus className="w-5 h-5" />
            DROP PIN HERE
          </Button>
          {position && !isValidGpsAccuracy(position.accuracy) && (
            <p className="text-xs text-primary text-center mt-2 bg-background/80 px-2 py-1 rounded">
              GPS accuracy must be ≤{PinValidation.accuracy.max}m
            </p>
          )}
        </div>
      </div>

      {/* Fixed Footer - Pin List + Ready Button */}
      <div className="bg-secondary border-t border-white/10 flex-shrink-0">
        {/* Pin list header */}
        {pins.length > 0 && (
          <div className="p-3 border-b border-white/10">
            <button 
              onClick={() => setIsPinListExpanded(!isPinListExpanded)}
              className="w-full flex items-center justify-between mb-2 hover:opacity-80 transition-opacity"
            >
              <h3 className="text-sm font-semibold">📍 Placed Pins</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-text-secondary">{pins.length} pin{pins.length !== 1 ? 's' : ''}</span>
                {isPinListExpanded ? (
                  <ChevronDown className="w-4 h-4 text-text-secondary" />
                ) : (
                  <ChevronUp className="w-4 h-4 text-text-secondary" />
                )}
              </div>
            </button>
            {/* Scrollable pin list - expands when toggled */}
            <div className={`${isPinListExpanded ? 'max-h-[300px]' : 'max-h-[88px]'} overflow-y-auto space-y-1 transition-all duration-200`}>
              {pins.map((pin, index) => (
                <PinCard
                  key={pin.id}
                  pin={pin}
                  index={index}
                  onEdit={() => handleEditPin(pin)}
                  onDelete={() => handleDeletePin(pin.id)}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDragEnd={handleDragEnd}
                  isDragging={dragIndex === index}
                  isDragOver={dragOverIndex === index}
                />
              ))}
            </div>
            {pins.length > 2 && !isPinListExpanded && (
              <p className="text-xs text-text-muted text-center mt-1">
                Tap to expand • Drag to reorder
              </p>
            )}
          </div>
        )}

        {/* Ready button */}
        <div className="p-3">
          <Button
            onClick={handleMarkReady}
            disabled={!canMarkReady || isMarkingReady}
            className="w-full flex items-center justify-center gap-2"
          >
            <Check className="w-5 h-5" />
            {isMarkingReady ? 'Marking ready...' : "I'M READY ✓"}
          </Button>
          {!canMarkReady && (
            <p className="text-xs text-text-secondary text-center mt-2">
              Place at least one pin to continue
            </p>
          )}
        </div>
      </div>

      {/* Participant Drawer */}
      <ParticipantDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        huntId={huntId || ''}
        participants={participants}
        currentUserId={currentUser?.uid || ''}
        creatorId={currentHunt?.createdBy || ''}
        onParticipantKicked={(userId) => {
          setParticipants(prev => prev.filter(p => p.odifiedUserId !== userId));
        }}
      />

      {/* Pin configuration modal */}
      <PinConfigModal
        position={pendingPin}
        editingPin={editingPin}
        onSave={handleSavePin}
        onCancel={() => {
          setPendingPin(null);
          setEditingPin(null);
        }}
        isSaving={isSaving}
      />

      {/* Hunt deleted modal */}
      {huntDeletedInfo && (
        <HuntDeletedModal
          huntName={huntDeletedInfo.huntName}
          message={huntDeletedInfo.message}
          onDismiss={() => {
            navigate('/', { replace: true });
          }}
        />
      )}
    </div>
  );
};

export default HiderPage;
